import { ApiHttpClient } from "../http/api-http-client";
import { HttpRequest } from "../http/http-request";
import { AccessTokenDto } from "./access-token.dto";
import { LoginDto } from "./login.dto";
import { LoginResultDto } from "./login-result.dto";
import { TotpSetupResponseDto } from "./totp-setup-response.dto";
import type { WebauthnRegistrationOptionsDto } from "./webauthn-registration-options.dto";
import type { WebauthnAuthenticationOptionsDto } from "./webauthn-authentication-options.dto";
import type { AuthenticationResponseJSON, RegistrationResponseJSON } from "@simplewebauthn/browser";

export class AuthResource {
  protected resourceUrl = "/api/auth";

  constructor(private readonly apiHttpClient: ApiHttpClient) {}

  async login(email: string, password: string): Promise<LoginResultDto> {
    return this.apiHttpClient.request<LoginDto, LoginResultDto>(
      HttpRequest.create({ url: this.resourceUrl + "/login", method: "POST", body: { email, password } })
    );
  }

  async requestPasswordReset(email: string): Promise<void> {
    return this.apiHttpClient.request<{ email: string }, void>(
      HttpRequest.create({ url: this.resourceUrl + "/request-password-reset", method: "POST", body: { email } })
    );
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    return this.apiHttpClient.request<{ token: string; newPassword: string }, void>(
      HttpRequest.create({ url: this.resourceUrl + "/reset-password", method: "POST", body: { token, newPassword } })
    );
  }

  async requestAccessCode(email: string): Promise<void> {
    return this.apiHttpClient.request<{ email: string }, void>(
      HttpRequest.create({ url: this.resourceUrl + "/request-access-code", method: "POST", body: { email } })
    );
  }

  async verifyAccessCode(email: string, code: string): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ email: string; code: string }, AccessTokenDto>(
      HttpRequest.create({ url: this.resourceUrl + "/verify-access-code", method: "POST", body: { email, code } })
    );
  }

  async setupTotp(ticket: string): Promise<TotpSetupResponseDto> {
    return this.apiHttpClient.request<{ ticket: string }, TotpSetupResponseDto>(
      HttpRequest.create({ url: this.resourceUrl + "/mfa/totp/setup", method: "POST", body: { ticket } })
    );
  }

  async getWebauthnRegistrationOptions(ticket: string): Promise<WebauthnRegistrationOptionsDto> {
    return this.apiHttpClient.request<{ ticket: string }, WebauthnRegistrationOptionsDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/mfa/webauthn/registration/options",
        method: "POST",
        body: { ticket },
      })
    );
  }

  async verifyWebauthnRegistration(ticket: string, response: RegistrationResponseJSON): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ ticket: string; response: RegistrationResponseJSON }, AccessTokenDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/mfa/webauthn/registration/verify",
        method: "POST",
        body: { ticket, response },
      })
    );
  }

  async confirmTotp(ticket: string, code: string): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ ticket: string; code: string }, AccessTokenDto>(
      HttpRequest.create({ url: this.resourceUrl + "/mfa/totp/confirm", method: "POST", body: { ticket, code } })
    );
  }

  async verifyMfa(ticket: string, code: string): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ ticket: string; code: string }, AccessTokenDto>(
      HttpRequest.create({ url: this.resourceUrl + "/mfa/verify", method: "POST", body: { ticket, code } })
    );
  }

  async getWebauthnAuthenticationOptions(ticket: string): Promise<WebauthnAuthenticationOptionsDto> {
    return this.apiHttpClient.request<{ ticket: string }, WebauthnAuthenticationOptionsDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/mfa/webauthn/authentication/options",
        method: "POST",
        body: { ticket },
      })
    );
  }

  async verifyWebauthnAuthentication(ticket: string, response: AuthenticationResponseJSON): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ ticket: string; response: AuthenticationResponseJSON }, AccessTokenDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/mfa/webauthn/authentication/verify",
        method: "POST",
        body: { ticket, response },
      })
    );
  }

  async requestEmailFallback(ticket: string): Promise<void> {
    return this.apiHttpClient.request<{ ticket: string }, void>(
      HttpRequest.create({ url: this.resourceUrl + "/mfa/request-email-fallback", method: "POST", body: { ticket } })
    );
  }

  async verifyEmailFallback(ticket: string, code: string): Promise<AccessTokenDto> {
    return this.apiHttpClient.request<{ ticket: string; code: string }, AccessTokenDto>(
      HttpRequest.create({
        url: this.resourceUrl + "/mfa/verify-email-fallback",
        method: "POST",
        body: { ticket, code },
      })
    );
  }

  async logout(): Promise<void> {
    return this.apiHttpClient.request<null, void>(
      HttpRequest.create({ url: this.resourceUrl + "/logout", method: "POST", auth: true })
    );
  }
}
